/**
 * The agent builder (docs/superpowers/specs/2026-10-27-agent-builder-design.md): a build is a
 * Claude agent working for 10–30 minutes in a sandbox, so this starts one and then polls it.
 * Its own origin (a Lambda Function URL) — base 'agent'.
 */
import { ApiError, request } from './client';

export interface AgentImage {
  name: string;
  contentType: string;
  /** base64, no data: prefix */
  data: string;
}

export interface ProgressLine {
  id: string;
  at?: string;
  text: string;
  kind: 'note' | 'step' | 'check';
}

export type AgentBuildStatus = 'running' | 'finalizing' | 'live' | 'failed';

export interface AgentBuild {
  buildId: string;
  siteId: string;
  kind: 'create' | 'change';
  status: AgentBuildStatus;
  progress: ProgressLine[];
  url?: string;
  title?: string;
  summary?: string;
  error?: string;
  startedAt: string;
  finishedAt?: string;
}

export interface AgentSite {
  siteId: string;
  slug?: string | null;
  title?: string | null;
  kind?: string | null;
  updatedAt: string;
  currentBuildId?: string;
  pendingBuildId?: string;
  description?: string;
}

export function agentConfigured(): boolean {
  return Boolean(process.env.EXPO_PUBLIC_AGENT_BUILD_URL);
}

export async function startAgentBuild(prompt: string, images: AgentImage[]): Promise<{ buildId: string; siteId: string }> {
  return request('/agent/builds', { method: 'POST', base: 'agent', body: sendable({ prompt, images }), timeoutMs: 90_000 });
}

export function getAgentBuild(buildId: string): Promise<AgentBuild> {
  // Publishing happens inside the poll that first sees the build finished, which can take a while.
  return request(`/agent/builds/${buildId}`, { base: 'agent', timeoutMs: 120_000 });
}

export async function startAgentChange(siteId: string, message: string, images: AgentImage[]): Promise<{ buildId: string }> {
  return request(`/agent/sites/${siteId}/changes`, { method: 'POST', base: 'agent', body: sendable({ message, images }), timeoutMs: 90_000 });
}

/** What is left of this month's agent builds (owner, 2026-11-03: Monthly includes 5 new sites a month). */
export interface AgentAllowance {
  unlimited: boolean;
  sitesLeft: number;
  sitesPerMonth: number;
  changesLeft: number;
  /** yyyy-mm-dd, the first of next month (UTC). */
  resetsOn: string;
}

export async function listAgentSites(): Promise<AgentSite[]> {
  return (await loadStudio()).sites;
}

/** The studio's front page: the caller's sites, newest first, and their allowance. */
export async function loadStudio(): Promise<{ sites: AgentSite[]; allowance: AgentAllowance | null }> {
  const res = await request<{ sites: AgentSite[]; allowance?: AgentAllowance }>('/agent/sites', { base: 'agent' });
  const sites = [...(res.sites ?? [])].sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
  return { sites, allowance: res.allowance ?? null };
}

export function getAgentSite(siteId: string): Promise<AgentSite> {
  return request(`/agent/sites/${siteId}`, { base: 'agent' });
}

/**
 * The live address of a site. Studio sites live under app.saddle-sites.com (the server's
 * SITES_DOMAIN); `<slug>.saddle-sites.com` does not resolve, and was shown here until 2026-11-04.
 */
export const SITES_DOMAIN = 'app.saddle-sites.com';
export function siteUrl(slug: string): string {
  return `https://${slug}.${SITES_DOMAIN}`;
}

/** The phone screenshot the agent took of a build (public, immutable). */
export function buildShotUrl(slug: string, buildId: string): string {
  return `${siteUrl(slug)}/_b/${buildId}/_saddle/phone.png`;
}

/*
 * How big a picture may be (2026-11-04). A Lambda function URL refuses a request over 6 MB before
 * the function runs, and without CORS headers, so the browser reports "could not reach the
 * server" and nothing is logged anywhere. Two iPhone screenshots sent untouched (the old rule kept
 * anything under 3.5 MB) did exactly that. Base64 makes each picture a third larger, so 680 KB is
 * about 0.9 MB in the request, and six of them still fit with the words.
 */
export const IMAGE_MAX_BYTES = 680 * 1024;
export const REQUEST_MAX_BYTES = 5_900_000;
const MAX_SIDE = 2000;
const SENT_AS_IS = new Set(['image/png', 'image/jpeg', 'image/webp']);

/** A decoded picture that can be drawn again at a scale, as a type, at a quality. */
export interface Drawable {
  width: number;
  height: number;
  render(scale: number, type: string, quality?: number): Promise<Blob>;
}

async function decode(file: Blob): Promise<Drawable> {
  const bitmap = await createImageBitmap(file);
  return {
    width: bitmap.width,
    height: bitmap.height,
    render(scale, type, quality) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const ctx = canvas.getContext('2d')!;
      // A JPEG has no transparency: a see-through screenshot would otherwise turn black.
      if (type === 'image/jpeg') {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      return new Promise((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not read that image'))), type, quality),
      );
    },
  };
}

/**
 * A picture no larger than IMAGE_MAX_BYTES. A small PNG, JPEG or WebP goes as it is. A screenshot
 * stays PNG when a PNG of it fits (cloning wants exact colours); anything else, iPhone HEIC photos
 * especially, which the server cannot read, becomes a JPEG, at lower quality and then smaller
 * until it fits.
 */
export async function fitImage(file: Blob, open: (f: Blob) => Promise<Drawable> = decode): Promise<{ blob: Blob; type: string }> {
  const type = file.type.toLowerCase();
  if (SENT_AS_IS.has(type) && file.size <= IMAGE_MAX_BYTES) return { blob: file, type };
  const img = await open(file);
  let scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
  if (type === 'image/png') {
    const png = await img.render(scale, 'image/png');
    if (png.size <= IMAGE_MAX_BYTES) return { blob: png, type: 'image/png' };
  }
  for (let round = 0; round < 8; round++) {
    for (const quality of [0.9, 0.8, 0.7]) {
      const jpeg = await img.render(scale, 'image/jpeg', quality);
      if (jpeg.size <= IMAGE_MAX_BYTES) return { blob: jpeg, type: 'image/jpeg' };
    }
    scale *= 0.75;
  }
  throw new Error('That picture is too large to send. Try a smaller screenshot.');
}

/** A picked file, ready to send: see fitImage. */
export async function prepareImage(file: File): Promise<AgentImage> {
  const { blob, type } = await fitImage(file);
  const base = (file.name || 'image').replace(/\.[^.]+$/, '');
  const name = blob === file ? file.name || 'image' : `${base}.${type === 'image/png' ? 'png' : 'jpg'}`;
  return { name, contentType: type, data: await toBase64(blob) };
}

/** The size of a request body as it will be sent. */
export function requestSize(body: unknown): number {
  return new TextEncoder().encode(JSON.stringify(body)).length;
}

/**
 * Refused here, in words, rather than sent and refused by the address with no CORS headers, which
 * the browser can only report as "could not reach the server".
 */
function sendable<T>(body: T): T {
  if (requestSize(body) > REQUEST_MAX_BYTES) {
    throw new ApiError('refused', 'Those pictures are too large to send together. Remove one and try again.');
  }
  return body;
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read that image'));
    reader.readAsDataURL(blob);
  });
}

export interface PublicSite {
  slug: string;
  title: string;
  description?: string;
  shotUrl: string;
  remixable: boolean;
}

/** What a "make your own" page shows about the site it came from (no sign-in needed). */
export function getPublicSite(slug: string): Promise<PublicSite> {
  return request(`/agent/public/${slug}`, { base: 'agent' });
}

/** Your own version of someone else's site, starting from a copy of it. */
export async function startRemix(slug: string, message: string, images: AgentImage[]): Promise<{ buildId: string; siteId: string }> {
  return request('/agent/remix', { method: 'POST', base: 'agent', body: sendable({ slug, message, images }), timeoutMs: 90_000 });
}
